import React, { useState, useEffect } from 'react';
import { Search, Filter, TrendingUp, Sparkles } from 'lucide-react';
import { searchArtworks } from '../services/searchService';
import { ArtworkGrid } from '../components/artwork/ArtworkGrid';
import { CategoryTabs } from '../components/artwork/CategoryTabs';
import { Artwork, Category, SearchFilters } from '../types';
import { motion } from 'framer-motion';

const Explore: React.FC = () => {
  const [artworks, setArtworks] = useState<Artwork[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [activeCategory, setActiveCategory] = useState<Category>('All');
  
  const [filters, setFilters] = useState<SearchFilters>({
    query: '',
    category: undefined,
    minPrice: undefined,
    maxPrice: undefined,
    sortBy: 'recent',
  });

  const categories: Category[] = ['All', 'Painting', 'Sculpture', 'Digital Art', 'Design', 'Handcraft'];

  useEffect(() => {
    handleSearch();
  }, [activeCategory, filters.sortBy]);

  const handleSearch = async () => {
    setLoading(true);
    try {
      const results = await searchArtworks({
        ...filters,
        query: searchQuery,
        category: activeCategory === 'All' ? undefined : activeCategory,
      });
      setArtworks(results);
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 pb-24">
      {/* Animated Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="text-yellow-500" size={28} />
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">
            Explore Art
          </h1>
        </div>
        <p className="text-gray-600">Discover amazing artworks from talented artists</p>
      </motion.div>

      {/* Search Bar */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex gap-2 mb-6"
      >
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search artworks, artists..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
          />
        </div>
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowFilters(!showFilters)}
          className={`p-3 rounded-xl border-2 transition-all ${
            showFilters 
              ? 'bg-primary text-white border-primary' 
              : 'border-gray-200 hover:border-primary'
          }`}
        >
          <Filter size={24} />
        </motion.button>
      </motion.div>

      {/* Filters Panel */}
      {showFilters && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-4 mb-6 shadow-lg"
        >
          <h3 className="font-bold text-gray-800 mb-4">Filters</h3>

          {/* Price Range */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Price Range (₹)</label>
            <div className="flex gap-2">
              <input
                type="number"
                placeholder="Min"
                value={filters.minPrice || ''}
                onChange={(e) => setFilters({ ...filters, minPrice: e.target.value ? Number(e.target.value) : undefined })}
                className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <input
                type="number"
                placeholder="Max"
                value={filters.maxPrice || ''}
                onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value ? Number(e.target.value) : undefined })}
                className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          {/* Sort By */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
            <select
              value={filters.sortBy}
              onChange={(e) => setFilters({ ...filters, sortBy: e.target.value as any })}
              className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="recent">Most Recent</option>
              <option value="popular">Most Popular</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
            </select>
          </div>

          <button
            onClick={handleSearch}
            className="w-full py-3 bg-primary text-white rounded-lg font-medium hover:bg-indigo-900 transition-colors"
          >
            Apply Filters
          </button>
        </motion.div>
      )}

      {/* Categories */}
      <div className="mb-6">
        <CategoryTabs
          categories={categories}
          activeCategory={activeCategory}
          onCategoryChange={setActiveCategory}
        />
      </div>

      {/* Trending Badge */}
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="text-orange-500" size={20} />
        <span className="text-sm font-medium text-gray-700">
          {artworks.length} {artworks.length === 1 ? 'artwork' : 'artworks'} found
        </span>
      </div>

      {/* Results */}
      <ArtworkGrid artworks={artworks} loading={loading} />
    </div>
  );
};

export default Explore;